'use client';

import React, { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface PricingCardsProps {
  billing: 'monthly' | 'annual';
}

const plans = [
  {
    id: 'plan-free',
    name: 'Free',
    tagline: 'For quick, disposable use',
    monthlyPrice: 0,
    annualPrice: 0,
    storage: '1 GB',
    storageGb: 1,
    highlight: false,
    badge: null,
    cta: 'Get started free',
    ctaStyle: 'ghost' as const,
    features: [
      { text: '1 temp address at a time', included: true },
      { text: 'Address expires in 24 hours', included: true },
      { text: 'Custom domain selection', included: true },
      { text: '1 GB cloud drive', included: true },
      { text: 'Basic message search', included: true },
      { text: 'Permanent address', included: false },
      { text: 'Multiple active addresses', included: false },
      { text: 'Custom username (e.g. alex@mailnova.io)', included: false },
      { text: 'Priority inbox delivery', included: false },
      { text: 'API access', included: false },
    ],
  },
  {
    id: 'plan-pro',
    name: 'Pro',
    tagline: 'For developers and power users',
    monthlyPrice: 4,
    annualPrice: 3,
    storage: '25 GB',
    storageGb: 25,
    highlight: true,
    badge: 'Most popular',
    cta: 'Upgrade to Pro',
    ctaStyle: 'primary' as const,
    features: [
      { text: '3 permanent addresses', included: true },
      { text: 'Never expires', included: true },
      { text: 'Custom username selection', included: true },
      { text: '25 GB cloud drive', included: true },
      { text: 'Full-text message search', included: true },
      { text: 'Permanent address', included: true },
      { text: 'Multiple active addresses', included: true },
      { text: 'Priority inbox delivery', included: true },
      { text: 'API access (read-only)', included: false },
      { text: 'Team sharing', included: false },
    ],
  },
  {
    id: 'plan-business',
    name: 'Business',
    tagline: 'For teams and companies',
    monthlyPrice: 16,
    annualPrice: 12,
    storage: '200 GB',
    storageGb: 200,
    highlight: false,
    badge: null,
    cta: 'Start Business trial',
    ctaStyle: 'ghost' as const,
    features: [
      { text: 'Unlimited permanent addresses', included: true },
      { text: 'Never expires', included: true },
      { text: 'Custom domain (bring your own)', included: true },
      { text: '200 GB shared drive', included: true },
      { text: 'Full-text message search', included: true },
      { text: 'Permanent address', included: true },
      { text: 'Multiple active addresses', included: true },
      { text: 'Priority inbox delivery', included: true },
      { text: 'Full API access', included: true },
      { text: 'Team sharing (up to 10 seats)', included: true },
    ],
  },
];

export default function PricingCards({ billing }: PricingCardsProps) {
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const { user } = useAuth();

  const handleUpgrade = async (planId: string, planName: string) => {
    if (planName === 'Free') {
      toast.info('Already on the free plan — no action needed');
      return;
    }
    setLoadingPlan(planId);
    try {
      const res = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planId,
          billing,
          userEmail: user?.email ?? null,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.url) {
        throw new Error(data.error ?? 'Failed to start checkout');
      }

      window.location.href = data.url;
    } catch (err: any) {
      console.error('Checkout error:', err);
      toast.error(err.message ?? 'Something went wrong. Please try again.');
      setLoadingPlan(null);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 xl:gap-8">
      {plans.map((plan) => {
        const price = billing === 'annual' ? plan.annualPrice : plan.monthlyPrice;
        const isLoading = loadingPlan === plan.id;

        return (
          <div
            key={plan.id}
            className={`relative flex flex-col rounded-2xl p-6 transition-all duration-200 ${
              plan.highlight
                ? 'shine-border shadow-glow-primary'
                : 'card-elevated hover:border-primary/30'
            }`}
          >
            {plan.badge && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="badge-permanent px-3 py-1 text-xs font-bold shadow-glow-primary">
                  {plan.badge}
                </span>
              </div>
            )}

            {/* Plan header */}
            <div className="mb-6">
              <h3 className="text-lg font-bold text-foreground mb-1">{plan.name}</h3>
              <p className="text-sm text-muted-foreground">{plan.tagline}</p>
            </div>

            {/* Price */}
            <div className="mb-6">
              <div className="flex items-end gap-1">
                <span className="text-4xl font-bold text-foreground tabular-nums">
                  ${price}
                </span>
                <span className="text-sm text-muted-foreground mb-1.5">
                  {price === 0 ? '/ forever' : '/ mo'}
                </span>
              </div>
              {billing === 'annual' && plan.monthlyPrice > 0 && (
                <p className="text-xs text-accent font-semibold mt-1">
                  Billed annually — save ${(plan.monthlyPrice - plan.annualPrice) * 12}/yr
                </p>
              )}
              {plan.monthlyPrice === 0 && (
                <p className="text-xs text-muted-foreground mt-1">No credit card required</p>
              )}
            </div>

            {/* Storage bar */}
            <div className="mb-6 p-3 rounded-xl bg-muted/60 border border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-muted-foreground">Cloud drive</span>
                <span className="text-xs font-bold text-foreground">{plan.storage}</span>
              </div>
              <div className="h-1.5 rounded-full bg-background overflow-hidden">
                <div
                  className={`h-full rounded-full ${plan.highlight ? 'bg-primary' : 'bg-muted-foreground/40'}`}
                  style={{ width: `${Math.min(100, (plan.storageGb / 200) * 100)}%` }}
                />
              </div>
            </div>

            {/* Features */}
            <ul className="space-y-2.5 mb-8 flex-1">
              {plan.features.map((f) => (
                <li key={`${plan.id}-feat-${f.text}`} className="flex items-start gap-2.5">
                  {f.included ? (
                    <span className="mt-0.5 w-4 h-4 rounded-full bg-accent/10 border border-accent/30 flex items-center justify-center flex-shrink-0">
                      <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                        <path d="M2 5L4 7L8 3" stroke="#4FFFB0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  ) : (
                    <span className="mt-0.5 w-4 h-4 rounded-full bg-muted border border-border flex items-center justify-center flex-shrink-0">
                      <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                        <path d="M3 3L7 7M7 3L3 7" stroke="#6B6B85" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </span>
                  )}
                  <span className={`text-sm leading-snug ${f.included ? 'text-foreground' : 'text-muted-foreground line-through decoration-muted-foreground/40'}`}>
                    {f.text}
                  </span>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <button
              onClick={() => handleUpgrade(plan.id, plan.name)}
              disabled={isLoading}
              className={`w-full py-3 rounded-xl font-semibold text-sm transition-all duration-150 flex items-center justify-center gap-2 ${
                plan.ctaStyle === 'primary' ?'btn-primary' :'btn-ghost'
              } ${isLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Redirecting to Stripe...
                </>
              ) : (
                plan.cta
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
}